import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { streamChat } from "../services/ChatService";
import { resizeImageFile, humanSize } from "../common/imageUtils";
import {
  uploadChatImage,
  uploadChatFile,
  toAbsoluteUploadUrl,
} from "../services/UploadService";
import {
  fetchChatList,
  fetchChatById,
  createChat,
  renameChatById,
  deleteAllChats,
  deleteChatById,
  setPinned as apiSetPinned,
  isObjectId,
  mapServerRowToConversation,
  mapDbMessagesToUi,
  editMessageContent,
  deleteFromMessage,
  branchConversation as apiBranchConversation,
  saveChatMessages,
  getProject,
} from "../services/ChatHistoryService";
import { MODELS } from "../constants/models";

const MODEL_STORAGE_KEY = "chatbot:selectedModel";
const PROJECT_STORAGE_KEY = "chatbot:currentProjectId";
const THINKING_MODE_KEY = "chatbot:thinkingMode";
const DEFAULT_MODEL = "claude-opus-4-8";
const THINKING_MODES = new Set(["thinking", "auto", "fast", "research"]);

function makeId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Đọc File → base64 thuần (không kèm tiền tố "data:...;base64,").
 * Dùng để gửi nguyên bytes file cho AI (backend đẩy thẳng lên OSS).
 */
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || "");
        const comma = result.indexOf(",");
        resolve(comma >= 0 ? result.slice(comma + 1) : result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    } catch (e) {
      reject(e);
    }
  });
}

/**
 * Convert a data URL (base64) → File để upload multipart lên server.
 * Trả về null nếu không phải data URL hợp lệ.
 */
function dataUrlToFile(dataUrl, filename = "image.png") {
  try {
    const m = /^data:([^;,]+)?(;base64)?,(.*)$/is.exec(dataUrl || "");
    if (!m) return null;
    const mime = (m[1] || "image/png").toLowerCase();
    const isBase64 = !!m[2];
    const dataPart = m[3] || "";
    const byteString = isBase64
      ? atob(dataPart)
      : decodeURIComponent(dataPart);
    const len = byteString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = byteString.charCodeAt(i);
    const ext = (mime.split("/")[1] || "png").replace("+xml", "");
    const safeName = /\.[a-z0-9]+$/i.test(filename)
      ? filename
      : `${filename}.${ext}`;
    return new File([bytes], safeName, { type: mime });
  } catch (e) {
    console.error("dataUrlToFile:", e);
    return null;
  }
}

/**
 * Upload danh sách ảnh (đang ở dạng base64 trong pendingFiles) lên server,
 * trả về mảng URL tuyệt đối đã lưu. Ảnh nào upload lỗi sẽ fallback base64.
 */
async function uploadPendingImages(imageFiles) {
  const urls = [];
  for (const f of imageFiles) {
    try {
      const file = dataUrlToFile(f.base64Url, f.name || "image.png");
      if (!file) {
        urls.push(f.base64Url);
        continue;
      }
      const res = await uploadChatImage(file);
      const rel = res?.data?.imageUrl;
      urls.push(rel ? toAbsoluteUploadUrl(rel) : f.base64Url);
    } catch (e) {
      console.error("uploadChatImage:", e);
      // Fallback: vẫn hiển thị được trong phiên hiện tại bằng base64.
      urls.push(f.base64Url);
    }
  }
  return urls;
}

/**
 * Upload danh sách file tài liệu (giữ File gốc trong pendingFiles) lên server.
 * Trả về mảng { url, name, mimeType, size } — file nào lỗi sẽ bị bỏ qua.
 */
async function uploadPendingDocs(docFiles) {
  const out = [];
  for (const f of docFiles) {
    if (!f?.file) continue;
    try {
      const res = await uploadChatFile(f.file);
      const data = res?.data || {};
      const rel = data.fileUrl;
      if (!rel) continue;
      out.push({
        url: toAbsoluteUploadUrl(rel),
        name: data.originalname || f.name || "file",
        mimeType: data.mimetype || f.mimeType || "",
        size: Number(data.size) || f.file.size || 0,
      });
    } catch (e) {
      console.error("uploadChatFile:", e);
      // Bỏ qua file lỗi, không chặn cả tin nhắn.
    }
  }
  return out;
}

export function useChat() {
  const navigate = useNavigate();
  const { id: urlId } = useParams();

  const validModelValues = useMemo(() => new Set(MODELS.map((m) => m.id)), []);
  const modelById = useMemo(() => {
    const map = new Map();
    MODELS.forEach((m) => map.set(m.id, m));
    return map;
  }, []);

  const [messages, setMessages] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState(() => {
    try {
      const savedModel = localStorage.getItem(MODEL_STORAGE_KEY);
      if (!savedModel) return DEFAULT_MODEL;
      const byId = MODELS.find((m) => m.id === savedModel);
      if (byId) return byId.id;
      const byValue = MODELS.find((m) => m.value === savedModel);
      return byValue?.id || DEFAULT_MODEL;
    } catch {
      return DEFAULT_MODEL;
    }
  });
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);

  // Thinking mode: "thinking" | "auto" | "fast" (lưu localStorage)
  const [thinkingMode, setThinkingModeState] = useState(() => {
    try {
      const saved = localStorage.getItem(THINKING_MODE_KEY);
      return saved && THINKING_MODES.has(saved) ? saved : "auto";
    } catch {
      return "auto";
    }
  });

  const setThinkingMode = useCallback((mode) => {
    const next = THINKING_MODES.has(mode) ? mode : "auto";
    setThinkingModeState(next);
    try {
      localStorage.setItem(THINKING_MODE_KEY, next);
    } catch { }
  }, []);

  // Project / folder filter
  const [currentProjectId, setCurrentProjectId] = useState(() => {
    try {
      return localStorage.getItem(PROJECT_STORAGE_KEY) || "all";
    } catch {
      return "all";
    }
  });

  // Editing message inline
  const [editingMessageId, setEditingMessageId] = useState(null);

  const messagesScrollRef = useRef(null);
  const textareaRef = useRef(null);
  const abortStreamRef = useRef(null);
  const streamRunIdRef = useRef(0);

  const scrollMessagesToBottom = useCallback((behavior = "smooth") => {
    const el = messagesScrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  useEffect(() => {
    scrollMessagesToBottom("smooth");
  }, [messages, scrollMessagesToBottom]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        200,
      )}px`;
    }
  }, [input]);

  useEffect(() => {
    if (!validModelValues.has(selectedModel)) {
      setSelectedModel(DEFAULT_MODEL);
      return;
    }
    try {
      localStorage.setItem(MODEL_STORAGE_KEY, selectedModel);
    } catch { }
  }, [selectedModel, validModelValues]);

  useEffect(() => {
    try {
      localStorage.setItem(PROJECT_STORAGE_KEY, currentProjectId || "all");
    } catch { }
  }, [currentProjectId]);

  const selectedModelConfig = modelById.get(selectedModel) || MODELS[0];
  const selectedModelValue = selectedModelConfig?.value || MODELS[0].value;

  /**
   * projectId dùng khi TẠO chat mới — chỉ nhận ObjectId, bỏ "all" / "unassigned".
   * "unassigned" nghĩa là user muốn xem chat chưa phân loại → khi tạo mới vẫn là null.
   */
  const projectIdForCreate = useMemo(() => {
    if (!currentProjectId || currentProjectId === "all" || currentProjectId === "unassigned") {
      return null;
    }
    return currentProjectId;
  }, [currentProjectId]);

  // Prompt riêng của project đang mở — inject làm system message khi chat.
  // Luôn fetch mới mỗi khi đổi project để dùng prompt mới nhất.
  const [currentProjectPrompt, setCurrentProjectPrompt] = useState("");
  useEffect(() => {
    let cancelled = false;
    if (!projectIdForCreate) {
      setCurrentProjectPrompt("");
      return;
    }
    (async () => {
      try {
        const res = await getProject(projectIdForCreate);
        if (cancelled) return;
        setCurrentProjectPrompt(res?.data?.prompt || "");
      } catch {
        if (!cancelled) setCurrentProjectPrompt("");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectIdForCreate]);

  const loadMessagesForConversation = useCallback(async (id) => {
    setPendingFiles([]);
    setEditingMessageId(null);
    if (!id || !isObjectId(id)) {
      setMessages([]);
      return;
    }
    try {
      const res = await fetchChatById(id);
      const doc = res?.data;
      setMessages(mapDbMessagesToUi(doc?.messages || []));
    } catch {
      setMessages([]);
    }
  }, []);

  const refreshConversationList = useCallback(
    async (projectIdOverride) => {
      try {
        const projectId = projectIdOverride ?? currentProjectId;
        const res = await fetchChatList({
          page: 1,
          limit: 100,
          projectId: projectId === "all" ? undefined : projectId,
        });
        setConversations((res?.data || []).map(mapServerRowToConversation));
      } catch (e) {
        console.error("refreshConversationList:", e);
      }
    },
    [currentProjectId],
  );

  // Hydrate
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchChatList({ page: 1, limit: 100 });
        if (cancelled) return;
        const rows = res?.data || [];
        if (rows.length > 0) {
          const mapped = rows.map(mapServerRowToConversation);
          setConversations(mapped);
          const targetId =
            urlId && mapped.find((c) => c.id === urlId) ? urlId : mapped[0].id;
          setActiveConversation(targetId);
          await loadMessagesForConversation(targetId);
          if (!urlId && mapped[0].id) {
            navigate(`/chatbot-ai/${mapped[0].id}`, { replace: true });
          }
        }
      } catch (e) {
        console.error("useChat hydrate:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload list khi project filter thay đổi
  useEffect(() => {
    refreshConversationList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProjectId]);

  // ── Pending files ─────────────────────────────────────────────────────────

  const removePendingFile = useCallback((id) => {
    setPendingFiles((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const handlePickedFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    for (const file of files) {
      const { name, type } = file;

      if (type.startsWith("image/")) {
        try {
          const result = await resizeImageFile(file, {
            maxDim: 1600,
            maxBytes: 500 * 1024, // dưới 500KB thì giữ nguyên
            quality: 0.85,
          });

          if (
            result.originalSize > result.newSize &&
            result.originalSize > 500 * 1024
          ) {
            console.log(
              `[image] Resized ${name}: ${humanSize(result.originalSize)} → ${humanSize(result.newSize)}`,
            );
          }

          setPendingFiles((prev) => [
            ...prev,
            {
              id: makeId(),
              name,
              kind: "image",
              base64Url: result.base64Url,
              mimeType: result.mimeType,
            },
          ]);
        } catch (err) {
          console.error("resize image:", err);
          toast.error(`Không đọc được ảnh ${name}`);
        }
        continue;
      }

      // Các loại còn lại đều coi là "doc": KHÔNG đọc/parse nội dung ở UI.
      // Chỉ giữ lại File gốc để upload lên server, rồi gửi URL cho AI xử lý.
      setPendingFiles((prev) => [
        ...prev,
        { id: makeId(), name, kind: "doc", file, mimeType: type || "" },
      ]);
      toast.success(`Đã thêm: ${name}`);
    }
  }, []);

  // ── Core: run stream with given messages array ────────────────────────────
  /**
   * Low-level runner: chạy stream với apiMessages đã build sẵn, hiển thị bubble assistant.
   * Dùng chung cho handleSend / regenerate / edit-and-resend / branch-continue.
   */
  const runStream = useCallback(
    async ({
      apiMessages,
      conversationId,
      title,
      modelId = selectedModel,
      modelValue = selectedModelValue,
      // Nội dung user message cần lưu kèm assistant reply. Khi null (regenerate)
      // chỉ lưu assistant. Khi là string → lưu cặp user + assistant.
      persistUserContent = null,
      // URL ảnh (đã upload server) cần lưu kèm user message.
      persistUserImages = null,
      // File tài liệu (đã upload server) cần lưu kèm user message.
      persistUserFiles = null,
    }) => {
      if (!apiMessages || apiMessages.length === 0) return;

      setError(null);
      setIsLoading(true);

      const assistantId = Date.now() + Math.random();
      setMessages((prev) => [
        ...prev,
        {
          id: assistantId,
          role: "assistant",
          content: "",
          thinkingContent: "",
          timestamp: new Date().toISOString(),
          model: modelId,
          streaming: true,
        },
      ]);

      let accumulated = "";
      let accumulatedThinking = "";

      const updateStreamingAssistant = (patch) => {
        setMessages((prev) =>
          prev.map((m, index) => {
            const isTargetAssistant =
              m.id === assistantId ||
              (index === prev.length - 1 &&
                m.role === "assistant" &&
                m.streaming);
            return isTargetAssistant ? { ...m, ...patch } : m;
          }),
        );
      };

      const runId = Date.now();
      streamRunIdRef.current = runId;
      const isCurrentRun = () => streamRunIdRef.current === runId;

      const convIdForStream = isObjectId(conversationId) ? conversationId : null;
      const prevActiveId = conversationId;

      abortStreamRef.current = streamChat({
        model: modelValue,
        messages: apiMessages,
        conversationId: convIdForStream,
        title,
        projectId: projectIdForCreate,
        thinkingMode,
        onChunk: (chunk) => {
          if (!isCurrentRun()) return;
          const delta = String(chunk ?? "");
          if (!delta) return;
          accumulated += delta;
          updateStreamingAssistant({ content: accumulated });
        },
        onThinkingChunk: (thinking) => {
          if (!isCurrentRun()) return;
          const delta = String(thinking ?? "");
          if (!delta) return;
          accumulatedThinking += delta;
          updateStreamingAssistant({ thinkingContent: accumulatedThinking });
        },
        onDone: async () => {
          if (!isCurrentRun()) return;
          abortStreamRef.current = null;
          updateStreamingAssistant({ streaming: false });
          setIsLoading(false);

          // Sau khi stream xong → lưu lịch sử vào chatbot-ai-server.
          // Endpoint provider (/anthropic/v1/messages) là stateless nên FE
          // chủ động persist cặp (user, assistant) hoặc chỉ assistant (regenerate).
          const assistantText = accumulated;
          if (!assistantText || !assistantText.trim()) {
            // Không có nội dung trả lời → không lưu, chỉ refresh list.
            refreshConversationList();
            return;
          }

          const toSave = [];
          if (persistUserContent != null) {
            toSave.push({
              role: "user",
              content: persistUserContent,
              images:
                Array.isArray(persistUserImages) && persistUserImages.length > 0
                  ? persistUserImages
                  : undefined,
              files:
                Array.isArray(persistUserFiles) && persistUserFiles.length > 0
                  ? persistUserFiles
                  : undefined,
            });
          }
          toSave.push({
            role: "assistant",
            content: assistantText,
            model: modelValue,
          });

          let savedId = isObjectId(conversationId) ? conversationId : null;
          try {
            const res = await saveChatMessages({
              conversationId: savedId,
              title: title || "Chat mới",
              model: modelValue,
              projectId: projectIdForCreate,
              messages: toSave,
            });
            const returnedId = res?.data?.conversationId;
            if (returnedId) savedId = returnedId;
          } catch (e) {
            console.error("saveChatMessages:", e);
            toast.error(
              e?.response?.data?.message || "Lỗi lưu lịch sử chat",
            );
          }

          if (savedId && savedId !== prevActiveId) {
            setConversations((prev) =>
              prev.map((c) =>
                c.id === prevActiveId ? { ...c, id: savedId } : c,
              ),
            );
            setActiveConversation(savedId);
            navigate(`/chatbot-ai/${savedId}`);
          }
          refreshConversationList();
          // Reload để lấy dbId thật của message vừa insert.
          if (savedId) {
            loadMessagesForConversation(savedId);
          } else if (isObjectId(conversationId)) {
            loadMessagesForConversation(conversationId);
          }
        },
        onError: (err) => {
          if (!isCurrentRun()) return;
          abortStreamRef.current = null;

          if (err?.name === "AbortError" || err?.message === "REQUEST_ABORTED") {
            setIsLoading(false);
            return;
          }

          setError(err.message || "Không thể kết nối tới server.");
          setMessages((prev) => prev.filter((m) => m.id !== assistantId));
          console.error("Chat stream error:", err);
          setIsLoading(false);
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedModel, selectedModelValue, navigate, refreshConversationList, loadMessagesForConversation, projectIdForCreate, thinkingMode],
  );

  // ── Send ──────────────────────────────────────────────────────────────────

  // ── External system prompt (injected by PIMATH tools) ───────────────────
  const [externalSystemPrompt, setExternalSystemPrompt] = useState("");

  // Gộp prompt project + prompt PIMATH thành các system message đầu hội thoại.
  // Project prompt đứng trước (base instruction theo ngữ cảnh dự án).
  const buildSystemMessages = useCallback(() => {
    const blocks = [];
    if (currentProjectPrompt && currentProjectPrompt.trim()) {
      blocks.push({ role: "system", content: currentProjectPrompt.trim() });
    }
    if (externalSystemPrompt && externalSystemPrompt.trim()) {
      blocks.push({ role: "system", content: externalSystemPrompt.trim() });
    }
    return blocks;
  }, [currentProjectPrompt, externalSystemPrompt]);

  const handleSend = async () => {
    const textPart = input.trim();
    const imageFiles = pendingFiles.filter((f) => f.kind === "image");
    const docFiles = pendingFiles.filter((f) => f.kind === "doc");

    const hasContent =
      textPart || docFiles.length > 0 || imageFiles.length > 0;
    if (!hasContent || isLoading) return;

    // Văn bản gửi AI là phần text người dùng gõ (không nhồi nội dung file).
    const fullTextForApi = textPart;

    // Upload ảnh/file lên server CHỈ để LƯU + hiển thị lại sau reload (URL bền vững).
    // KHÔNG gửi URL cho AI — luôn gửi base64 để OSS (chatbot-apikey) tự xử lý.
    let persistedImageUrls = [];
    if (imageFiles.length > 0) {
      persistedImageUrls = await uploadPendingImages(imageFiles);
    }

    let persistedDocs = [];
    if (docFiles.length > 0) {
      persistedDocs = await uploadPendingDocs(docFiles);
    }

    // Đọc file thành base64 để GỬI THẲNG bytes cho AI (backend đẩy lên OSS),
    // không phụ thuộc việc tải lại từ URL.
    let apiDocBlocks = [];
    if (docFiles.length > 0) {
      apiDocBlocks = await Promise.all(
        docFiles.map(async (f) => {
          try {
            if (!f.file) return null;
            const data = await fileToBase64(f.file);
            if (!data) return null;
            return {
              type: "file",
              data, // base64 thuần (không có "data:" prefix)
              name: f.name || "file",
              mimeType: f.mimeType || f.file.type || "application/octet-stream",
            };
          } catch (e) {
            console.error("fileToBase64:", e);
            return null;
          }
        }),
      );
      apiDocBlocks = apiDocBlocks.filter(Boolean);
    }

    let lastUserContent;
    const hasAttachments = imageFiles.length > 0 || apiDocBlocks.length > 0;
    if (hasAttachments) {
      lastUserContent = [
        ...(fullTextForApi ? [{ type: "text", text: fullTextForApi }] : []),
        // Ảnh: gửi base64 (data URI) cho AI, KHÔNG gửi URL server.
        ...imageFiles.map((f) => ({
          type: "image_url",
          image_url: { url: f.base64Url },
        })),
        // File: gửi base64 — backend chatbot-apikey đẩy thẳng lên Qwen OSS.
        ...apiDocBlocks,
      ];
      if (lastUserContent.length === 0) {
        lastUserContent = " ";
      }
    } else {
      lastUserContent = fullTextForApi;
    }

    const userMessage = {
      id: Date.now(),
      role: "user",
      content: textPart,
      // Hiển thị bằng URL server (bền vững) nếu upload thành công, fallback base64.
      images:
        persistedImageUrls.length > 0
          ? persistedImageUrls
          : imageFiles.map((f) => f.base64Url),
      // Chip file kèm URL tải về (bền vững sau reload).
      attachedDocs:
        persistedDocs.length > 0
          ? persistedDocs.map((d) => ({ name: d.name, url: d.url }))
          : docFiles.map((f) => ({ name: f.name })),
      apiContent: lastUserContent,
      timestamp: new Date().toISOString(),
    };

    const currentInput =
      textPart || docFiles[0]?.name || imageFiles[0]?.name || "Chat mới";

    let targetConversationId = activeConversation;
    let justCreatedTitle = null; // Title cho conversation vừa mới auto-create
    if (!isObjectId(targetConversationId)) {
      const autoTitle =
        currentInput.slice(0, 40) + (currentInput.length > 40 ? "..." : "");
      try {
        const res = await createChat({
          title: autoTitle,
          model: selectedModelValue,
          projectId: projectIdForCreate,
        });
        const created = mapServerRowToConversation(res?.data || {});
        targetConversationId = created.id;
        justCreatedTitle = created.title || autoTitle;
        setConversations((prev) => [
          created,
          ...prev.filter((c) => c.id !== created.id),
        ]);
        setActiveConversation(created.id);
        navigate(`/chatbot-ai/${created.id}`);
      } catch (e) {
        console.error("auto createChat in handleSend:", e);
        toast.error("Không tạo được chat mới");
        return;
      }
    }

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setPendingFiles([]);
    setError(null);

    const currentConv = conversations.find((c) => c.id === targetConversationId);
    let conversationTitleForSave =
      justCreatedTitle || currentConv?.title || "Chat mới";

    // Nếu conversation đã tồn tại với title "Chat mới" và chưa có message nào,
    // auto đổi title theo input user.
    if (
      !justCreatedTitle &&
      currentConv &&
      currentConv.title === "Chat mới" &&
      messages.length === 0
    ) {
      const newTitle =
        currentInput.slice(0, 40) + (currentInput.length > 40 ? "..." : "");
      conversationTitleForSave = newTitle;
      setConversations((prev) =>
        prev.map((c) =>
          c.id === targetConversationId ? { ...c, title: newTitle } : c,
        ),
      );
      if (isObjectId(targetConversationId)) {
        try {
          await renameChatById(targetConversationId, newTitle);
        } catch (e) {
          console.error("renameChatById:", e);
        }
      }
    }

    const apiMessages = [
      // Inject system prompt (project prompt + PIMATH prompt) nếu có
      ...buildSystemMessages(),
      ...messages.map((m) => ({
        role: m.role,
        content: m.apiContent !== undefined ? m.apiContent : m.content,
      })),
      { role: "user", content: lastUserContent },
    ];

    await runStream({
      apiMessages,
      conversationId: targetConversationId,
      title: conversationTitleForSave,
      persistUserContent: textPart || "",
      persistUserImages: persistedImageUrls,
      persistUserFiles: persistedDocs,
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const stopGenerating = useCallback(() => {
    streamRunIdRef.current += 1;
    const abort = abortStreamRef.current;
    abortStreamRef.current = null;
    if (typeof abort === "function") abort();

    setIsLoading(false);
    setError(null);

    setMessages((prev) => {
      const finished = prev.map((m) =>
        m.role === "assistant" && m.streaming
          ? { ...m, streaming: false, stopped: true }
          : m,
      );
      const last = finished[finished.length - 1];
      if (
        last?.role === "assistant" &&
        !String(last.content || "").trim() &&
        !String(last.thinkingContent || "").trim()
      ) {
        return finished.slice(0, -1);
      }
      return finished;
    });
  }, []);

  // ── Edit user message & resend ────────────────────────────────────────────

  /**
   * Bắt đầu edit 1 user message — mở textarea inline.
   */
  const startEditMessage = useCallback((messageUiId) => {
    if (isLoading) return;
    setEditingMessageId(messageUiId);
  }, [isLoading]);

  const cancelEditMessage = useCallback(() => {
    setEditingMessageId(null);
  }, []);

  /**
   * Save edit + resend: cập nhật content, xoá tất cả message sau nó, stream lại.
   */
  const saveEditAndResend = useCallback(
    async (messageUiId, newContent) => {
      if (isLoading) return;
      const target = messages.find((m) => m.id === messageUiId);
      if (!target || target.role !== "user") return;

      const newContentTrim = String(newContent || "").trim();
      if (!newContentTrim) {
        toast.error("Nội dung không được để trống");
        return;
      }

      // Build apiContent mới: giữ image/doc cũ nếu có, thay phần text
      let newApiContent;
      if (Array.isArray(target.apiContent)) {
        newApiContent = target.apiContent.map((part) =>
          part.type === "text" ? { ...part, text: newContentTrim } : part,
        );
        if (!newApiContent.some((p) => p.type === "text")) {
          newApiContent.unshift({ type: "text", text: newContentTrim });
        }
      } else {
        newApiContent = newContentTrim;
      }

      // Cập nhật UI ngay: edit message + xoá messages sau nó
      const idx = messages.findIndex((m) => m.id === messageUiId);
      if (idx === -1) return;
      const beforeAndIncluding = messages.slice(0, idx + 1);
      const updatedTarget = {
        ...target,
        content: newContentTrim,
        apiContent: newApiContent,
        edited: true,
        editedAt: new Date().toISOString(),
      };
      const newMessages = [...beforeAndIncluding.slice(0, idx), updatedTarget];
      setMessages(newMessages);
      setEditingMessageId(null);

      // Sync DB: edit + truncate
      if (isObjectId(activeConversation) && target.dbId) {
        try {
          await editMessageContent(activeConversation, target.dbId, newContentTrim);
          await deleteFromMessage(activeConversation, target.dbId, {
            inclusive: false, // giữ message edit, xoá các message sau nó
          });
        } catch (e) {
          console.error("editMessage/truncate:", e);
          toast.error(
            e?.response?.data?.message || "Lỗi đồng bộ chỉnh sửa với server",
          );
          return;
        }
      }

      // Build apiMessages để resend
      const apiMessages = [
        ...buildSystemMessages(),
        ...newMessages.map((m) => ({
          role: m.role,
          content: m.apiContent !== undefined ? m.apiContent : m.content,
        })),
      ];

      const convTitle =
        conversations.find((c) => c.id === activeConversation)?.title ||
        "Chat mới";

      await runStream({
        apiMessages,
        conversationId: activeConversation,
        title: convTitle,
      });
    },
    [
      isLoading,
      messages,
      activeConversation,
      conversations,
      runStream,
      buildSystemMessages,
    ],
  );

  // ── Regenerate assistant response ─────────────────────────────────────────

  /**
   * Regenerate: xoá assistant message đang có + mọi message sau nó, rồi stream lại.
   * Dùng user message liền trước làm điểm regen.
   */
  const regenerateFromAssistant = useCallback(
    async (assistantUiId) => {
      if (isLoading) return;
      const idx = messages.findIndex((m) => m.id === assistantUiId);
      if (idx === -1) return;

      const assistantMsg = messages[idx];
      if (assistantMsg.role !== "assistant") return;
      if (assistantMsg.streaming) {
        toast.info("Đang trả lời, không thể tạo lại lúc này");
        return;
      }

      // apiMessages = mọi message trước assistant đó
      const before = messages.slice(0, idx);
      if (before.length === 0 || before[before.length - 1].role !== "user") {
        toast.error("Không thể tạo lại: thiếu tin nhắn user phía trước");
        return;
      }

      setMessages(before);

      // Sync DB: xoá assistant + các message sau
      if (isObjectId(activeConversation) && assistantMsg.dbId) {
        try {
          await deleteFromMessage(activeConversation, assistantMsg.dbId, {
            inclusive: true,
          });
        } catch (e) {
          console.error("regenerate delete:", e);
          toast.error(
            e?.response?.data?.message || "Lỗi xoá message để tạo lại",
          );
        }
      }

      const apiMessages = [
        ...buildSystemMessages(),
        ...before.map((m) => ({
          role: m.role,
          content: m.apiContent !== undefined ? m.apiContent : m.content,
        })),
      ];

      const convTitle =
        conversations.find((c) => c.id === activeConversation)?.title ||
        "Chat mới";

      // Dùng model cũ nếu có, nếu không thì model hiện tại
      const prevModelId = assistantMsg.model;
      const prevModelCfg =
        (prevModelId && modelById.get(prevModelId)) ||
        (prevModelId &&
          MODELS.find((m) => m.value === prevModelId)) ||
        null;

      await runStream({
        apiMessages,
        conversationId: activeConversation,
        title: convTitle,
        modelId: prevModelCfg?.id || selectedModel,
        modelValue: prevModelCfg?.value || selectedModelValue,
      });
    },
    [
      isLoading,
      messages,
      activeConversation,
      conversations,
      runStream,
      selectedModel,
      selectedModelValue,
      modelById,
      buildSystemMessages,
    ],
  );

  // ── Branch from message ───────────────────────────────────────────────────

  /**
   * Tạo nhánh mới từ message (userMessage → clone tới message đó, rồi mở conversation mới).
   */
  const branchFromMessage = useCallback(
    async (messageUiId) => {
      if (isLoading) return;
      const target = messages.find((m) => m.id === messageUiId);
      if (!target) return;

      if (!isObjectId(activeConversation) || !target.dbId) {
        toast.error(
          "Chưa thể tạo nhánh — hãy gửi ít nhất một tin nhắn trước rồi thử lại",
        );
        return;
      }

      try {
        const res = await apiBranchConversation(activeConversation, {
          untilMessageId: target.dbId,
        });
        const data = res?.data;
        if (!data?.conversation?._id) throw new Error("Không nhận được nhánh mới");

        const newConv = mapServerRowToConversation(data.conversation);
        setConversations((prev) => [
          newConv,
          ...prev.filter((c) => c.id !== newConv.id),
        ]);
        setActiveConversation(newConv.id);
        setMessages(mapDbMessagesToUi(data.messages || []));
        setEditingMessageId(null);
        navigate(`/chatbot-ai/${newConv.id}`);
        toast.success("Đã tạo nhánh mới");
      } catch (e) {
        console.error("branch:", e);
        toast.error(
          e?.response?.data?.message || "Không tạo được nhánh",
        );
      }
    },
    [isLoading, messages, activeConversation, navigate],
  );

  // ── Pin / unpin ───────────────────────────────────────────────────────────

  const togglePinned = useCallback(
    async (conversationId) => {
      const conv = conversations.find((c) => c.id === conversationId);
      if (!conv) return;
      const nextPinned = !conv.pinned;

      // Optimistic
      setConversations((prev) =>
        prev.map((c) =>
          c.id === conversationId
            ? { ...c, pinned: nextPinned, pinnedAt: nextPinned ? new Date().toISOString() : null }
            : c,
        ),
      );

      try {
        if (isObjectId(conversationId)) {
          await apiSetPinned(conversationId, nextPinned);
          await refreshConversationList();
        }
      } catch (e) {
        console.error("togglePinned:", e);
        toast.error("Không đổi được trạng thái ghim");
        // Revert
        setConversations((prev) =>
          prev.map((c) =>
            c.id === conversationId ? { ...c, pinned: !nextPinned } : c,
          ),
        );
      }
    },
    [conversations, refreshConversationList],
  );

  // ── Feedback (like/dislike) — optimistic update message trong state ───────
  const applyMessageFeedback = useCallback((dbId, feedback) => {
    if (!dbId) return;
    setMessages((prev) =>
      prev.map((m) =>
        m.dbId === dbId ? { ...m, feedback } : m,
      ),
    );
  }, []);

  // ── Conversation management ───────────────────────────────────────────────

  const newChat = async () => {
    if (abortStreamRef.current) {
      abortStreamRef.current();
      abortStreamRef.current = null;
    }

    const existingNewChat = conversations.find(
      (c) => (c?.title || "").trim().toLowerCase() === "chat mới",
    );
    if (existingNewChat) {
      setActiveConversation(existingNewChat.id);
      setMessages([]);
      setPendingFiles([]);
      setIsLoading(false);
      setError(null);
      navigate(`/chatbot-ai/${existingNewChat.id}`);
      return;
    }

    try {
      const res = await createChat({
        title: "Chat mới",
        model: selectedModelValue,
        projectId: projectIdForCreate,
      });
      const created = mapServerRowToConversation(res?.data || {});
      setConversations((prev) => [
        created,
        ...prev.filter((c) => c.id !== created.id),
      ]);
      setActiveConversation(created.id);
      navigate(`/chatbot-ai/${created.id}`);
    } catch (e) {
      console.error("createChat:", e);
      toast.error("Không tạo được chat mới");
      return;
    }

    setMessages([]);
    setPendingFiles([]);
    setIsLoading(false);
    setError(null);
  };

  const switchConversation = (id) => {
    if (abortStreamRef.current) {
      abortStreamRef.current();
      abortStreamRef.current = null;
    }
    streamRunIdRef.current += 1;
    setActiveConversation(id);
    setPendingFiles([]);
    setError(null);
    setIsLoading(false);
    void loadMessagesForConversation(id);

    if (isObjectId(id)) {
      navigate(`/chatbot-ai/${id}`);
    } else {
      navigate("/chatbot-ai");
    }
  };

  const deleteConversation = async (id, e) => {
    if (e?.stopPropagation) e.stopPropagation();
    try {
      if (isObjectId(id)) {
        await deleteChatById(id);
      }
    } catch { }
    setConversations((prev) => {
      const next = prev.filter((c) => c.id !== id);
      if (next.length === 0) {
        setActiveConversation(null);
        setMessages([]);
        setPendingFiles([]);
        navigate("/chatbot-ai");
        return [];
      }
      if (activeConversation === id) {
        setActiveConversation(next[0].id);
        void loadMessagesForConversation(next[0].id);
        if (isObjectId(next[0].id)) {
          navigate(`/chatbot-ai/${next[0].id}`);
        } else {
          navigate("/chatbot-ai");
        }
      }
      return next;
    });
  };

  const clearHistory = async () => {
    if (abortStreamRef.current) {
      abortStreamRef.current();
      abortStreamRef.current = null;
    }
    try {
      await deleteAllChats();
    } catch (e) {
      console.error("clearHistory:", e);
    }
    setConversations([]);
    setActiveConversation(null);
    setMessages([]);
    setPendingFiles([]);
    setIsLoading(false);
    setError(null);
    navigate("/chatbot-ai");
  };

  const formatTime = (isoString) => {
    return new Date(isoString).toLocaleTimeString("vi-VN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "Asia/Ho_Chi_Minh",
    });
  };

  return {
    messages,
    pendingFiles,
    removePendingFile,
    handlePickedFiles,
    input,
    setInput,
    isLoading,
    selectedModel,
    setSelectedModel,
    error,
    sidebarOpen,
    setSidebarOpen,
    conversations,
    activeConversation,
    currentProjectId,
    setCurrentProjectId,
    currentProjectPrompt,
    setCurrentProjectPrompt,
    messagesScrollRef,
    textareaRef,
    handleSend,
    handleKeyDown,
    stopGenerating,
    newChat,
    switchConversation,
    deleteConversation,
    clearHistory,
    formatTime,
    // New actions
    editingMessageId,
    startEditMessage,
    cancelEditMessage,
    saveEditAndResend,
    regenerateFromAssistant,
    branchFromMessage,
    togglePinned,
    refreshConversationList,
    applyMessageFeedback,
    // PIMATH system prompt injection
    externalSystemPrompt,
    setExternalSystemPrompt,
    // Thinking mode (thinking | auto | fast)
    thinkingMode,
    setThinkingMode,
  };
}
